import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import {
    CreateReportPayload,
    MeterReplacementStatusRequest,
    NewServiceRequestPayload,
    ReportFilter,
    ReportIssuePayload,
    ReportWrongBalanceRequest,
    RequestChangeOfDetailsPayload,
    RequestMeterTransferPayload,
    RequestStatementPayload,
    RequestTemporaryDisconnectionPayload,
    ServiceRequestTimelineData,
} from '@/types'
import { RootState } from '../store'
import { ServiceRequestTimelineStatus } from '@/components/timeline/TimelineItem'
import { ServiceRequestTimelineStages, ServiceRequestComplaintTypes, ReportIssueTypes } from '@/constants/reports'
import ReportRepository from '@/repositories/report-repository'

export default class ReportService {
    constructor(private reportRepository: ReportRepository, private store: ToolkitStore<RootState>) {}

    getReports(
        filter: Partial<ReportFilter> = {
            Page: 1,
            PageSize: 100,
        },
    ) {
        return this.reportRepository.getReports(filter)
    }

    createReport(report: CreateReportPayload) {
        return this.reportRepository.createReport(report)
    }

    reportIssue(report: ReportIssuePayload) {
        return this.reportRepository.reportIssue(report)
    }

    fillNewServiceRequestForm(token: string, payload: NewServiceRequestPayload) {
        return this.reportRepository.fillNewServiceRequestForm(token, payload)
    }

    requestNewService(payload: NewServiceRequestPayload) {
        return this.reportRepository.requestNewService(payload)
    }

    requestChangeOfDetails(payload: RequestChangeOfDetailsPayload, paymentReferenceId?: string) {
        return this.reportRepository.requestChangeOfDetails(payload, paymentReferenceId)
    }

    requestStatement(payload: RequestStatementPayload) {
        return this.reportRepository.requestStatement(payload)
    }

    requestMeterTransfer(payload: RequestMeterTransferPayload) {
        return this.reportRepository.requestMeterTransfer(payload)
    }

    requestTemporaryDisconnection(payload: RequestTemporaryDisconnectionPayload) {
        return this.reportRepository.requestTemporaryDisconnection(payload)
    }

    requestReconnection(payload: { accountNumber: string }) {
        return this.reportRepository.requestReconnection(payload)
    }

    reportDelayedInstallation(payload: { accountNumber: string }) {
        return this.reportRepository.reportDelayedInstallation(payload)
    }

    async getServicRequestDetails(code: string) {
        return this.reportRepository.getServicRequestDetails({
            code,
            limit: 100,
            offset: 0,
        })
    }

    async persistServicRequestDetails(code: string) {
        return this.reportRepository.persistServicRequestDetails({
            code,
            limit: 100,
            offset: 0,
        })
    }

    getNewServiceProgressReport(requestCode: string) {
        return this.reportRepository.getNewServiceProgressReport(requestCode)
    }
    validateNewServiceRequestFormToken(token: string) {
        return this.reportRepository.validateNewServiceRequestFormToken(token)
    }
    async getServiceRequestTimeline(trackingCode: string) {
        return this.reportRepository.getServiceRequestTimeline(trackingCode)
    }

    async getCustomerStatementTimeline(trackingCode: string) {
        return this.reportRepository.getCustomerStatementTimeline(trackingCode)
    }

    async getCustomerStatementUrls(payload: { trackingCode?: string | null; paymentRef?: string | null }) {
        return this.reportRepository.getCustomerStatementUrls(payload)
    }

    getServiceRequestTimelineItemStatusClass(
        stage: ServiceRequestTimelineStages,
        timelineData: ServiceRequestTimelineData,
    ): ServiceRequestTimelineStatus {
        const statusCheckers = {
            [ServiceRequestTimelineStages.REQUESTED]: this.getRequestedStatus,
            [ServiceRequestTimelineStages.ASSIGNED]: this.getAssignedStatus,
            [ServiceRequestTimelineStages.SCHEDULED]: this.getScheduledStatus,
            [ServiceRequestTimelineStages.PAYMENT]: this.getPaymentStatus,
            [ServiceRequestTimelineStages.CARD_ISSUED]: this.getCardIssuedStatus,
            [ServiceRequestTimelineStages.CARD_PICKED_UP]: this.getCardPickedUpStatus,
            [ServiceRequestTimelineStages.COMPLETED]: this.getCompletedStatus,
        }

        return statusCheckers[stage]?.(timelineData) ?? ServiceRequestTimelineStatus.INACTIVE
    }

    private getRequestedStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        return timelineData?.requestSubmitted
            ? ServiceRequestTimelineStatus.COMPLETED
            : ServiceRequestTimelineStatus.INACTIVE
    }

    private getAssignedStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        return timelineData?.requestAssigned
            ? ServiceRequestTimelineStatus.COMPLETED
            : ServiceRequestTimelineStatus.INACTIVE
    }

    private getScheduledStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        if (timelineData?.schedule?.status === 'completed') {
            return ServiceRequestTimelineStatus.COMPLETED
        }
        if (timelineData?.schedule?.status === 'pending') {
            return ServiceRequestTimelineStatus.ACTIVE
        }
        return ServiceRequestTimelineStatus.INACTIVE
    }

    private getPaymentStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        const isScheduleCompleted = timelineData.schedule?.status === 'completed'
        const isInvoicePaid = timelineData?.invoiceInformation?.status === 'paid'

        if (isScheduleCompleted && timelineData?.invoiceInformation) {
            return ServiceRequestTimelineStatus.ACTIVE
        } else if (isInvoicePaid) {
            return ServiceRequestTimelineStatus.COMPLETED
        } else {
            return ServiceRequestTimelineStatus.INACTIVE
        }
    }

    private getCardIssuedStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        const isInvoicePaid = timelineData?.invoiceInformation?.status === 'paid'
        const isCardIssued = timelineData.cardIssued?.state === ServiceRequestTimelineStatus.COMPLETED

        if (isInvoicePaid && timelineData?.cardIssued) {
            return ServiceRequestTimelineStatus.ACTIVE
        } else if (isCardIssued) {
            return ServiceRequestTimelineStatus.COMPLETED
        } else {
            return ServiceRequestTimelineStatus.INACTIVE
        }
    }

    private getCardPickedUpStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        const isCardIssued = timelineData.cardIssued?.state === ServiceRequestTimelineStatus.COMPLETED
        const isCardPickedUp = timelineData.cardPickedUp?.state === ServiceRequestTimelineStatus.COMPLETED

        if (isCardIssued && timelineData?.cardPickedUp) {
            return ServiceRequestTimelineStatus.ACTIVE
        } else if (isCardPickedUp) {
            return ServiceRequestTimelineStatus.COMPLETED
        } else {
            return ServiceRequestTimelineStatus.INACTIVE
        }
    }

    private getCompletedStatus(timelineData: ServiceRequestTimelineData): ServiceRequestTimelineStatus {
        const isScheduleCompleted = timelineData.schedule?.status === 'completed'
        const isInvoicePaid = timelineData.invoiceInformation?.status === 'paid'
        const isRequestCompleted = timelineData.requestCompleted

        if ((isScheduleCompleted && isRequestCompleted) || (isInvoicePaid && isRequestCompleted)) {
            return ServiceRequestTimelineStatus.ACTIVE
        } else {
            return ServiceRequestTimelineStatus.INACTIVE
        }
    }

    getStatementRequestTimelineItemStatusClass(state?: string | null): ServiceRequestTimelineStatus {
        const statusMap: Record<string, ServiceRequestTimelineStatus> = {
            completed: ServiceRequestTimelineStatus.COMPLETED,
            pending: ServiceRequestTimelineStatus.ACTIVE,
            processing: ServiceRequestTimelineStatus.ACTIVE,
            failed: ServiceRequestTimelineStatus.FAILED,
            'refund pending': ServiceRequestTimelineStatus.ACTIVE,
            refunded: ServiceRequestTimelineStatus.COMPLETED,
            'refund failed': ServiceRequestTimelineStatus.COMPLETED,
            'delayed refund': ServiceRequestTimelineStatus.COMPLETED,
        }

        if (!state) {
            return ServiceRequestTimelineStatus.INACTIVE
        }
        return (
            Object.entries(statusMap).find(([key, value]) => key.includes(state))?.[1] ??
            ServiceRequestTimelineStatus.INACTIVE
        )
    }

    getStatementRequestTransactionTimelineStatus(state?: string | null): ServiceRequestTimelineStatus {
        const statusMap: Record<string, ServiceRequestTimelineStatus> = {
            completed: ServiceRequestTimelineStatus.COMPLETED,
            pending: ServiceRequestTimelineStatus.ACTIVE,
            processing: ServiceRequestTimelineStatus.ACTIVE,
            failed: ServiceRequestTimelineStatus.FAILED,
            'refund pending': ServiceRequestTimelineStatus.ACTIVE,
            refunded: ServiceRequestTimelineStatus.FAILED,
            'refund failed': ServiceRequestTimelineStatus.ACTIVE,
            'delayed refund': ServiceRequestTimelineStatus.ACTIVE,
        }

        if (!state) {
            return ServiceRequestTimelineStatus.INACTIVE
        }
        return (
            Object.entries(statusMap).find(([key, value]) => key.includes(state))?.[1] ??
            ServiceRequestTimelineStatus.INACTIVE
        )
    }

    getServiceRequestIcon = (complaintType: string, status?: string | null): string => {
        const IMAGE_MAP = {
            [ServiceRequestComplaintTypes.NEW_SERVICE_REQUEST]: {
                default: '/images/icons/service-request/request_new_service.png',
                // discarded: '/images/icons/service-request/request_new_service_discarded.png',
                completed: '/images/icons/service-request/request_new_service_completed.png',
                failed: '/images/icons/service-request/request_new_service_completed.png',
            },
            [ServiceRequestComplaintTypes.CHANGE_OF_ACCOUNT_DETAILS]: {
                default: '/images/icons/service-request/request_change_of_account.png',
                discarded: '/images/icons/service-request/request_change_of_account_discarded.png',
                completed: '/images/icons/service-request/request_change_of_account_completed.png',
            },
            [ServiceRequestComplaintTypes.STATEMENT_REQUEST]: {
                default: '/images/icons/service-request/request_customer_statement.png',
                discarded: '/images/icons/service-request/request_customer_statement_discarded.png',
                completed: '/images/icons/service-request/request_customer_statement_completed.png',
            },
            [ServiceRequestComplaintTypes.TEMPORARY_DISCONNECTION]: {
                default: '/images/icons/service-request/temp_disconnection.png',
                discarded: '/images/icons/service-request/temp_disconnection_discarded.png',
                completed: '/images/icons/service-request/temp_disconnection_completed.png',
            },
            [ServiceRequestComplaintTypes.METER_TRANSFER]: {
                default: '/images/icons/service-request/meter_transfer.png',
                discarded: '/images/icons/service-request/meter_transfer.png',
                completed: '/images/icons/service-request/meter_transfer_complete.png',
            },
            [ReportIssueTypes.PREPAID_METER_NOT_FOUND]: {
                default: '/images/icons/service-request/meter-not-found.png',
                discarded: '/images/icons/service-request/meter-not-found.png',
                completed: '/images/icons/service-request/meter-not-found.png',
            },
            [ReportIssueTypes.REPORT_FAULTY_METER]: {
                default: '/images/icons/service-request/faulty_meter.png',
                discarded: '/images/icons/service-request/faulty_meter.png',
                completed: '/images/icons/service-request/faulty_meter.png',
            },
            [ReportIssueTypes.STOLEN_METER]: {
                default: '/images/icons/service-request/stolen_meter.png',
                discarded: '/images/icons/service-request/stolen_meter.png',
                completed: '/images/icons/service-request/stolen_meter.png',
            },
            [ReportIssueTypes.REPORT_ILLEGAL_CONNECTION]: {
                default: '/images/icons/service-request/stolen_meter.png',
                discarded: '/images/icons/service-request/stolen_meter.png',
                completed: '/images/icons/service-request/stolen_meter.png',
            },
            [ReportIssueTypes.METER_CARD_REPLACEMENT]: {
                default: '/images/icons/service-request/meter_card_replacement.png',
                discarded: '/images/icons/service-request/stolen_meter.png',
                completed: '/images/icons/service-request/meter_card_replacement_completed.png',
            },
        } as Record<string, Record<string, string>>

        const complaintTypeKey = complaintType.toLowerCase()
        const statusKey = (status ?? '').toLowerCase()

        if (IMAGE_MAP[complaintTypeKey]) {
            return IMAGE_MAP[complaintTypeKey][statusKey ?? ''] || IMAGE_MAP[complaintTypeKey].default
        }

        return '/images/icons/service-request/user.png' // Default icon if no match is found
    }

    getReportStatusBgColorClass(status: string) {
        const STATUS_MAP = {
            completed: 'status-green',
            pending: 'status-yellow',
            rejected: 'status-red',
            'in-progress': 'status-yellow',
            discarded: 'status-red',
            refunded: 'status-green',
        } as Record<string, string>

        return STATUS_MAP[status?.toLowerCase()] || 'status-yellow'
    }

    reportDelayedRefundForStatement(payload: { paymentReferenceId?: string; trackingCode?: string }) {
        return this.reportRepository.reportDelayedRefundForStatement(payload)
    }

    checkMeterReplacementStatus(payload: MeterReplacementStatusRequest) {
        return this.reportRepository.checkMeterReplacementStatus(payload)
    }

    async reportWrongMeterBalance(payload: ReportWrongBalanceRequest) {
        return this.reportRepository.reportIssue(payload as unknown as ReportIssuePayload)
    }
}
